import { forwardRef, useEffect, useLayoutEffect, useRef } from 'react';
import type { PropsWithoutRef } from 'react';

import {
  useConsolidatedRef,
  useContentTabIndex,
  useEscape,
  useI18n,
  useOuterEvent,
  useUID
} from '../../hooks';
import Text from '../Text';
import Icon from '../Icon';
import Progress from '../Progress';
import Flex from '../Flex';
import SummaryItem from '../SummaryItem';
import { focusNonInteractiveElement, getActiveElement } from '../../utils';
import type { RefElement } from '../../types';

import { StyledChildrenWrap, StyledDialogContent, StyledDialogHeader } from './Dialog.styles';
import type { InfoDialogProps } from './Dialog.types';
import { StyledDismissButton, StyledInfoDialog } from './InfoDialog.styles';

const InfoDialog = forwardRef<RefElement<InfoDialogProps>, PropsWithoutRef<InfoDialogProps>>(
  function InfoDialog(
    { target, heading: headingProp, children, progress: progressProp, onDismiss, ...restProps },
    ref
  ) {
    const t = useI18n();
    const headingId = useUID();
    const headingRef = useRef<HTMLHeadingElement | HTMLSpanElement>(null);
    const headingText = typeof headingProp === 'string' ? headingProp : headingProp?.primary;
    const dialogRef = useConsolidatedRef(ref);
    const dialogContentRef = useRef<HTMLDivElement>(null);
    const contentTabIndex = useContentTabIndex(dialogRef, dialogContentRef, [], [progressProp]);

    const setFocus = () => {
      if (progressProp) return;
      if (headingRef.current) {
        focusNonInteractiveElement(headingRef.current, headingText);
      } else if (dialogRef.current) {
        focusNonInteractiveElement(dialogRef.current, headingText);
      }
    };

    useEscape(
      e => {
        onDismiss?.();
        e.stopPropagation();
      },
      dialogRef,
      [onDismiss]
    );

    useOuterEvent('mousedown', [target, dialogRef], () => {
      onDismiss?.();
    });

    // Using layout effect to capture the focused element prior to dialog elements taking focus.
    useLayoutEffect(() => {
      const initiatorElement = getActiveElement();

      return () => {
        if (initiatorElement instanceof HTMLElement && initiatorElement.isConnected) {
          initiatorElement.focus();
        }
      };
    }, []);

    useEffect(() => {
      if (!progressProp) {
        setFocus();
      }
    }, [progressProp]);

    useEffect(() => {
      const closeDialog = () => {
        setTimeout(() => {
          onDismiss?.();
          // The 0 timeout ensures that the dialog will not re-open immediately after the close event is triggered.
        }, 0);
      };

      target.addEventListener('click', closeDialog);

      return () => {
        target.removeEventListener('click', closeDialog);
      };
    }, []);

    const heading = headingProp ? (
      <Text ref={headingRef} id={headingId} variant='h2'>
        {headingText}
      </Text>
    ) : null;

    const progress = (
      <Progress
        visible={!!progressProp}
        focusOnVisible
        placement='local'
        message={typeof progressProp === 'string' ? progressProp : undefined}
      />
    );

    const dismissButton = (
      <StyledDismissButton
        icon
        variant='simple'
        onClick={() => {
          onDismiss();
        }}
        label={t('close')}
      >
        <Icon name='times' />
      </StyledDismissButton>
    );

    return (
      <StyledInfoDialog
        {...restProps}
        {...(typeof headingProp === 'string'
          ? { 'aria-labelledby': headingId }
          : { 'aria-label': headingProp?.primary })}
        arrow
        target={target}
        progress={progressProp}
        ref={dialogRef}
      >
        {headingProp && heading && (
          <Flex
            container={{ alignItems: 'center', justify: 'between', pad: [1, 2], gap: 1 }}
            item={{ shrink: 0 }}
            as={StyledDialogHeader}
          >
            {typeof headingProp === 'string' ? (
              heading
            ) : (
              <SummaryItem
                ref={headingRef}
                primary={heading}
                secondary={
                  headingProp.secondary ? (
                    <Text variant='secondary'>{headingProp.secondary}</Text>
                  ) : undefined
                }
                visual={headingProp.visual}
              />
            )}
            {dismissButton}
          </Flex>
        )}

        {(!headingProp || children || progressProp) && (
          <Flex
            container={{ direction: 'column' }}
            item={{ grow: 1 }}
            as={StyledDialogContent}
            ref={dialogContentRef}
            tabIndex={contentTabIndex}
          >
            {!headingProp && dismissButton}
            {children && (
              <StyledChildrenWrap inert={progressProp ? '' : undefined}>
                {children}
              </StyledChildrenWrap>
            )}
            {progress}
          </Flex>
        )}
      </StyledInfoDialog>
    );
  }
);

export default InfoDialog;
